import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Aperture, Download, Lock, LogOut, RefreshCw, ArrowLeft } from "lucide-react";
import { API } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";

const OWNER_EMAILS = ["getskills@darrelboye.com", "darrelboye@gmail.com"];

export default function Downloads() {
  const { user, loading, login, logout } = useAuth();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [fetching, setFetching] = useState(false);

  const isOwner = user && OWNER_EMAILS.includes((user.email || "").toLowerCase());

  const load = useCallback(async () => {
    setFetching(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/resume/downloads`, { withCredentials: true });
      setRows(data);
    } catch (e) {
      setError(e?.response?.status === 403 ? "This account is not authorized." : "Failed to load downloads.");
      setRows([]);
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    if (isOwner) load();
  }, [isOwner, load]);

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString(undefined, {
        year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div data-testid="downloads-page" className="min-h-screen bg-[#050505] text-white">
      <div className="grain-overlay" />
      <div className="max-w-[1200px] mx-auto px-6 sm:px-10 py-10">
        <div className="flex items-center justify-between mb-12">
          <Link to="/" data-testid="downloads-home-link" className="flex items-center gap-3 group">
            <Aperture className="w-7 h-7 text-[#D90429] group-hover:rotate-90 transition-transform duration-700" />
            <span className="font-head text-xl tracking-[0.25em] uppercase">Darrel Boye</span>
          </Link>
          {user && (
            <button onClick={logout} data-testid="downloads-logout" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-[#EF233C] transition-colors">
              <LogOut className="w-4 h-4" /> Log out
            </button>
          )}
        </div>

        <p className="font-body text-xs uppercase tracking-[0.3em] text-gray-500 mb-3">Owner Dashboard</p>
        <h1 className="font-head uppercase text-4xl sm:text-6xl tracking-tighter leading-none flex items-center gap-4">
          <Download className="w-8 h-8 text-[#EF233C]" /> Resume Downloads
        </h1>

        {/* Not signed in */}
        {!loading && !user && (
          <div data-testid="downloads-signin" className="mt-16 border border-white/10 bg-[#0f0f10] p-10 max-w-md">
            <Lock className="w-8 h-8 text-[#D90429] mb-5" />
            <h2 className="font-head uppercase text-2xl tracking-tight">Owner Access Only</h2>
            <p className="font-body text-gray-400 mt-3">Sign in with your Google owner account to view who downloaded your resume.</p>
            <button onClick={() => login("/downloads")} data-testid="downloads-signin-btn" className="mt-8 inline-flex items-center gap-3 px-8 py-4 bg-[#D90429] hover:bg-[#EF233C] transition-colors">
              <Lock className="w-4 h-4" />
              <span className="font-body uppercase tracking-[0.15em] text-sm">Sign in with Google</span>
            </button>
          </div>
        )}

        {/* Signed in, not owner */}
        {!loading && user && !isOwner && (
          <div data-testid="downloads-denied" className="mt-16 border border-white/10 bg-[#0f0f10] p-10 max-w-md">
            <h2 className="font-head uppercase text-2xl tracking-tight">Access Denied</h2>
            <p className="font-body text-gray-400 mt-3">
              Signed in as <span className="text-white">{user.email}</span>, which isn&apos;t the owner account.
            </p>
            <button onClick={logout} className="mt-6 inline-flex items-center gap-2 text-sm text-gray-400 hover:text-[#EF233C]">
              <LogOut className="w-4 h-4" /> Switch account
            </button>
          </div>
        )}

        {/* Owner view */}
        {isOwner && (
          <div className="mt-12">
            <div className="flex items-center justify-between mb-6">
              <p className="font-body text-sm text-gray-400" data-testid="downloads-count">
                {rows ? `${rows.length} total download${rows.length === 1 ? "" : "s"}` : "Loading..."}
              </p>
              <button onClick={load} data-testid="downloads-refresh" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors">
                <RefreshCw className={`w-4 h-4 ${fetching ? "animate-spin" : ""}`} /> Refresh
              </button>
            </div>

            {error && <p className="text-[#EF233C] font-body text-sm mb-4">{error}</p>}

            <div className="border border-white/10 overflow-hidden">
              <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-white/[0.03] border-b border-white/10 font-body text-xs uppercase tracking-[0.15em] text-gray-500">
                <span className="col-span-4">Name</span>
                <span className="col-span-4">Email</span>
                <span className="col-span-3">When</span>
                <span className="col-span-1 text-right">IP</span>
              </div>
              {rows && rows.length === 0 && (
                <p className="px-6 py-10 text-center text-gray-500 font-body">No downloads yet.</p>
              )}
              {rows && rows.map((r, i) => (
                <div key={i} data-testid={`download-row-${i}`} className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                  <span className="col-span-4 font-body text-sm text-white truncate">{r.name || "—"}</span>
                  <span className="col-span-4 font-body text-sm text-gray-400 truncate">{r.email}</span>
                  <span className="col-span-3 font-body text-sm text-gray-400">{fmt(r.downloaded_at)}</span>
                  <span className="col-span-1 font-body text-xs text-gray-600 text-right truncate">{r.ip || "—"}</span>
                </div>
              ))}
            </div>

            <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors mt-8">
              <ArrowLeft className="w-4 h-4" /> Back to site
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
